import {
    PromotedProductDTO,
    UserFacingHazardStage,
} from '@hazadapt-git/public-core-base'
import { AxiosResponse } from 'axios'

import { isa } from '../api'
import { getLocalJSON, setLocalJSON } from '../browser-storage'

const GUEST_PRODUCT_BOOKMARKS_KEY = 'hazadapt_product_bookmarks'

export const getGuestBookmarkedProducts = (): PromotedProductDTO[] =>
    getLocalJSON<PromotedProductDTO[]>(GUEST_PRODUCT_BOOKMARKS_KEY, []) ?? []

export const addGuestProductBookmark = (product: PromotedProductDTO): void => {
    const products = getGuestBookmarkedProducts()
    if (products.some((item) => item.id === product.id)) return

    if (
        !setLocalJSON(GUEST_PRODUCT_BOOKMARKS_KEY, [
            ...products,
            { ...product, bookmarked: true },
        ])
    ) {
        throw new Error('Unable to save product bookmark locally.')
    }
}

export const deleteGuestProductBookmark = (product_id: number): void => {
    const products = getGuestBookmarkedProducts().filter(
        (product) => product.id !== product_id
    )
    if (!setLocalJSON(GUEST_PRODUCT_BOOKMARKS_KEY, products)) {
        throw new Error('Unable to remove product bookmark locally.')
    }
}

/**
 * Get community sponsors for a specified hazard
 * @param hazard_id The hazard to query for
 * @param stage The active hazard stage
 * @param point The user's current location, formatted as [lat, lng]
 * @returns PromotedProductDTO[]
 */
export const fetchPromotedProducts = async (
    hazard_id: number,
    stage: UserFacingHazardStage,
    logged_in: boolean,
    point?: [number, number]
): Promise<PromotedProductDTO[]> =>
    new Promise<PromotedProductDTO[]>((resolve, reject) => {
        isa.get(
            `/public/v2/${
                logged_in ? 'authenticated/' : ''
            }promoted-products/${hazard_id}?stage=${stage}${
                point ? `&lat=${point[0]}&lng=${point[1]}` : ''
            }`
        )
            .then((response) => {
                if (response.status !== 200) {
                    reject(response.data?.error || 'Unknown API error')
                } else {
                    resolve(response.data)
                }
            })
            .catch((err) => {
                console.error(err)
                reject(err)
            })
    })

export const getPromotedProducts = (
    hazard_id: number,
    stage: UserFacingHazardStage,
    logged_in: boolean
) =>
    new Promise<PromotedProductDTO[]>((resolve, reject) => {
        fetchPromotedProducts(hazard_id, stage, logged_in)
            .then(resolve)
            .catch(reject)
    })

export const getPromotedProductsByIds = async (
    product_ids: number | number[],
    logged_in: boolean
): Promise<PromotedProductDTO[]> => {
    const ids = Array.isArray(product_ids) ? product_ids : [product_ids]
    if (ids.length === 0) return []

    const response: AxiosResponse<PromotedProductDTO[]> = await isa.get(
        `/public/v2/${
            logged_in ? 'authenticated/' : ''
        }promoted-products/by-id/${ids.join(',')}`
    )
    return response.data
}

export const getBookmarkedProducts = async (): Promise<
    PromotedProductDTO[]
> => {
    try {
        const response: AxiosResponse<PromotedProductDTO[]> = await isa.get(
            '/public/v2/bookmarks/products'
        )
        return response.data
    } catch (err) {
        console.error(err)
        return []
    }
}

export const addProductBookmark = async (
    product_id: number
): Promise<number> => {
    const response: AxiosResponse<number> = await isa.post(
        `/public/v2/bookmarks/products/${product_id}`
    )
    return response.data
}

export const deleteProductBookmark = async (
    product_id: number
): Promise<number> => {
    const response: AxiosResponse<number> = await isa.delete(
        `/public/v2/bookmarks/products/${product_id}`
    )
    return response.data
}
